import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFeatureToggle from 'framework/featureToggle';
import { useEmployeeAvailabilityApi } from 'infrastructure/api';
import { Features } from 'infrastructure/featureToggle';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import {
  AVAILABILITY_WS_EVENTS,
  AVAILABILITY_WS_INITIAL_RECONNECT_DELAY_MS,
  AVAILABILITY_WS_MAX_RECONNECT_DELAY_MS,
  AVAILABILITY_WS_STABLE_CONNECTION_MS,
} from './constants';
import { emitAvailabilitySocketEvent } from './events';
import { buildAvailabilitySocketUrl, isSocketSupported, toInboundText } from './helpers';
import { dispatchAvailabilitySocketMessage } from './messages';
import {
  AvailabilitySocketConnectionState,
  AvailabilitySocketContextValue,
  AvailabilitySocketMessageType,
} from './types';

type AvailabilitySocketProviderProps = PropsWithChildren<{
  isAvailabilityEnabled: boolean;
}>;

const unavailableSocketContextValue: AvailabilitySocketContextValue = {
  connectionState: AvailabilitySocketConnectionState.Disabled,
  sendMessage: async () => {
    throw new Error('Availability websocket is not connected');
  },
};

export const AvailabilitySocketContext = createContext<AvailabilitySocketContextValue>(unavailableSocketContextValue);

const AvailabilitySocketProvider = ({ children, isAvailabilityEnabled }: AvailabilitySocketProviderProps) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const { isAuthenticated } = useAuthenticationProvider();
  const [connectionState, setConnectionState] = useState<AvailabilitySocketConnectionState>(
    AvailabilitySocketConnectionState.Disabled,
  );
  const socketRef = useRef<WebSocket | null>(null);
  const enableAvailabilityFeature = isFeatureEnabled(Features.INTERRUPTIONS);
  const { baseUrl: apiBaseUrl } = useEmployeeAvailabilityApi();
  const { sendPostRequest } = useEmployeeAvailabilityApi();
  const enabled = Boolean(
    isAvailabilityEnabled && enableAvailabilityFeature && isAuthenticated && apiBaseUrl && isSocketSupported(),
  );

  const sendMessage = useCallback(async (messageType: AvailabilitySocketMessageType, payload: unknown = {}) => {
    const socket = socketRef.current;

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      throw new Error('Availability websocket is not connected');
    }

    socket.send(
      JSON.stringify({
        type: messageType,
        payload,
      }),
    );
  }, []);

  useEffect(() => {
    if (!enabled) {
      socketRef.current?.close(1000, 'Availability websocket disabled');
      socketRef.current = null;
      setConnectionState(AvailabilitySocketConnectionState.Disabled);
      return undefined;
    }

    let disposed = false;
    let reconnectDelayMs = AVAILABILITY_WS_INITIAL_RECONNECT_DELAY_MS;
    let openedAtMs: number | null = null;
    let reconnectTimeoutId: number | null = null;
    let reconnectNotificationSent = false;

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutId !== null) {
        window.clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
    };

    const setStateIfActive = (nextState: AvailabilitySocketConnectionState) => {
      if (!disposed) {
        setConnectionState(nextState);
      }
    };

    const requestSocketUrl = async () => {
      const request = await sendPostRequest('/employee-availability/ws-ticket');
      const response = await request.json();

      return buildAvailabilitySocketUrl(apiBaseUrl, response.webSocketUrl);
    };

    const scheduleReconnect = (reason: unknown, connect: () => void) => {
      if (disposed) {
        return;
      }

      if (openedAtMs !== null && Date.now() - openedAtMs >= AVAILABILITY_WS_STABLE_CONNECTION_MS) {
        reconnectDelayMs = AVAILABILITY_WS_INITIAL_RECONNECT_DELAY_MS;
      }
      openedAtMs = null;
      // Jitter spreads the reconnects of all open tabs, e.g. right after a deploy.
      const delayMs = Math.round(reconnectDelayMs / 2 + Math.random() * (reconnectDelayMs / 2));
      reconnectDelayMs = Math.min(reconnectDelayMs * 2, AVAILABILITY_WS_MAX_RECONNECT_DELAY_MS);

      setStateIfActive(AvailabilitySocketConnectionState.Reconnecting);

      if (!reconnectNotificationSent) {
        reconnectNotificationSent = true;
        emitAvailabilitySocketEvent(
          AVAILABILITY_WS_EVENTS.RECONNECTING,
          `WebSocket reconnect scheduled in ${Math.round(delayMs / 1000)}s: ${
            reason instanceof Error ? reason.message : String(reason)
          }`,
        );
      }

      clearReconnectTimeout();
      reconnectTimeoutId = window.setTimeout(connect, delayMs);
    };

    const connect = () => {
      clearReconnectTimeout();
      setStateIfActive(
        reconnectNotificationSent
          ? AvailabilitySocketConnectionState.Reconnecting
          : AvailabilitySocketConnectionState.Connecting,
      );

      void requestSocketUrl()
        .then(wsUrl => {
          if (disposed) {
            return;
          }

          const socket = new WebSocket(wsUrl);
          socketRef.current = socket;

          socket.onopen = () => {
            if (disposed) {
              return;
            }

            openedAtMs = Date.now();
            setStateIfActive(AvailabilitySocketConnectionState.Connected);
            emitAvailabilitySocketEvent(AVAILABILITY_WS_EVENTS.CONNECTED, undefined);

            if (reconnectNotificationSent) {
              reconnectNotificationSent = false;
              emitAvailabilitySocketEvent(AVAILABILITY_WS_EVENTS.RESTORED, undefined);
            }
          };

          socket.onmessage = event => {
            void toInboundText(event.data).then(text => {
              if (text !== null) {
                dispatchAvailabilitySocketMessage(text);
              }
            });
          };

          socket.onerror = () => {
            if (socket.readyState !== WebSocket.CLOSED && socket.readyState !== WebSocket.CLOSING) {
              socket.close();
            }
          };

          socket.onclose = event => {
            if (socketRef.current === socket) {
              socketRef.current = null;
            }

            if (disposed) {
              return;
            }

            setStateIfActive(AvailabilitySocketConnectionState.Disconnected);
            emitAvailabilitySocketEvent(AVAILABILITY_WS_EVENTS.DISCONNECTED, undefined);

            scheduleReconnect(`WS close ${event.code}${event.reason ? `: ${event.reason}` : ''}`, connect);
          };
        })
        .catch(reason => {
          if (disposed) {
            return;
          }

          scheduleReconnect(reason, connect);
        });
    };

    connect();

    return () => {
      disposed = true;
      clearReconnectTimeout();

      const socket = socketRef.current;
      socketRef.current = null;

      if (socket && socket.readyState !== WebSocket.CLOSED && socket.readyState !== WebSocket.CLOSING) {
        socket.close(1000, 'Availability websocket stopped');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, enabled]);

  const value = useMemo(
    () => ({
      connectionState,
      sendMessage,
    }),
    [connectionState, sendMessage],
  );

  return <AvailabilitySocketContext.Provider value={value}>{children}</AvailabilitySocketContext.Provider>;
};

export default AvailabilitySocketProvider;
